import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { showToast } from '@mercai/clever'

import { SelectStatus } from '../select-status'

import { ModalDeliveryProof } from './components'

import { LogisticShipmentProps, LogisticShipmentStatusEnum } from '@/types'

import { shipmentOptions } from './select-logistic-shipment-status.constants'

import { useOrderCenterSaleOrderUpdate } from '@/services'
import { SelectLogisticShipmentStatusProps } from './select-logistic-shipment-status.type'

export const SelectLogisticShipmentStatus = ({
  saleOrderUuid,
  shipment,
  size = 'medium',
  queryKeys,
}: SelectLogisticShipmentStatusProps) => {
  const { t: tEnums } = useTranslation('enums')

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(saleOrderUuid)

  const queryClient = useQueryClient()

  const [isOpenDeliveryProofWarningModal, setIsOpenDeliveryProofWarningModal] =
    useState(false)

  const onUpdateShipmentStatus = async (
    status: LogisticShipmentProps['status'],
    forceUpdate?: boolean,
  ) => {
    const shipments: LogisticShipmentProps[] = []

    const hasProofOfDelivery = shipment?.proofOfDelivery !== null

    if (
      status === LogisticShipmentStatusEnum.delivered &&
      !hasProofOfDelivery &&
      !forceUpdate
    ) {
      setIsOpenDeliveryProofWarningModal(true)
      return
    }

    shipments.push({
      uuid: shipment?.uuid,
      status,
    } as LogisticShipmentProps)

    const response = await mutateAsync({
      shipments,
    })

    if (response?.success) {
      queryKeys?.map((key) =>
        queryClient.invalidateQueries({
          queryKey: key,
        }),
      )
    } else {
      showToast({
        variant: 'error',
        title: response.message || '',
      })
    }
  }

  return (
    <>
      <SelectStatus
        size={size}
        status={shipment?.status}
        options={shipmentOptions(tEnums)}
        onChange={async (status) =>
          await onUpdateShipmentStatus(
            status as LogisticShipmentProps['status'],
          )
        }
      />
      <ModalDeliveryProof
        saleOrderUuid={saleOrderUuid}
        shipment={shipment}
        isOpen={isOpenDeliveryProofWarningModal}
        onSaveAfterUpload={async () => {
          await onUpdateShipmentStatus(
            LogisticShipmentStatusEnum.delivered,
            true,
          )
          setIsOpenDeliveryProofWarningModal(false)
        }}
        onClose={() => {
          setIsOpenDeliveryProofWarningModal(false)
        }}
      />
    </>
  )
}
